import { ComponentProps, FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Data, Controls } from '@pcmp/react-coworker-components';
import { ListBoxFooter } from '@ingka/list-box';
import Button from '@ingka/button';
import chevronDown from '@ingka/ssr-icon/paths/chevron-down';

import styles from './index.module.css';

const { PillDropdown } = Data;
const { DateRangePicker } = Controls;

const ConfirmFooter: FC<{
  onClick: () => void;
}> = ({ onClick }) => {
  const { t } = useTranslation();

  return (
    <ListBoxFooter>
      <Button
        fluid
        size="small"
        text={t('label.apply')}
        type="primary"
        onClick={onClick}
        data-testid="applyDate"
      />
    </ListBoxFooter>
  );
};

export const DropdownDatePicker: FC<{
  label: string;
  value: { from?: number; to?: number };
  onChange: (date?: { from?: number; to?: number }) => void;
  position?: ComponentProps<typeof PillDropdown>['position'];
  confirm?: boolean;
  disabled?: boolean;
  dayPicker?: ComponentProps<typeof DateRangePicker>['dayPicker'];
}> = ({ label, value, onChange, position, confirm, disabled, dayPicker }) => {
  const [open, setOpen] = useState(false);
  const [dateDraft, setDateDraft] = useState<{ from?: number; to?: number }>(
    value
  );
  const onClose = () => {
    setOpen(false);
    setDateDraft(value);
  };
  const onToggle = () => setOpen(!open);
  const handleChange = useCallback(
    (dates?: { from?: Date; to?: Date }) => {
      if (confirm) {
        setDateDraft({
          from: dates?.from?.getTime(),
          to: dates?.to?.getTime(),
        });
      } else {
        onChange({
          from: dates?.from?.getTime(),
          to: dates?.to?.getTime(),
        });
      }
    },
    [onChange, confirm]
  );
  const onConfirmValue = useCallback(() => {
    onChange(dateDraft);
    setOpen(false);
  }, [onChange, dateDraft]);

  // Update draft if root state changes
  useEffect(() => {
    setDateDraft({
      from: value.from,
      to: value.to,
    });
  }, [value]);

  return (
    <PillDropdown
      position={position}
      pill={{
        disabled,
        className: styles['pill-dropdown--button'],
        iconPosition: 'trailing',
        label,
        size: 'small',
        ssrIcon: chevronDown,
      }}
      open={open}
      onClick={onToggle}
      onClose={onClose}
      listBox={{
        className: `mt-3 ${styles['list-box__body']}`,
        footer: confirm && <ConfirmFooter onClick={onConfirmValue} />,
      }}
    >
      <div className="p-3 flex justify-center">
        <DateRangePicker
          from={dateDraft.from ? new Date(dateDraft.from) : undefined}
          to={dateDraft.to ? new Date(dateDraft.to) : undefined}
          onChange={handleChange}
          dayPicker={dayPicker}
        />
      </div>
    </PillDropdown>
  );
};
